/**
 * Le planning général : toutes les contre-études validées sur un seul calendrier.
 *
 * *PRD §4.7 (v1.3). Demande de Chris, 15/08/2026 : « ce planning général va être composé
 * de toutes les contre-études validées ».*
 *
 * Deux lectures d'une même réponse, l'une sous l'autre :
 *
 *  1. **la frise** — un chantier par ligne, un jour ouvré par colonne : où sont les
 *     chantiers, et lesquels tiennent sur une date hypothèse ;
 *  2. **le plan de charge** — un code générique par ligne : combien d'heures chaque jour
 *     demande, tous chantiers confondus. C'est la grandeur du §4.7, celle qui se compare
 *     à la capacité du référentiel.
 *
 * ## Les jours chômés ne sont pas des colonnes
 *
 * La frise n'affiche que les jours ouvrés — week-ends, fériés et jours de fermeture
 * paramétrés (§4.4) sautent. Les afficher vides ferait croire à des trous de charge là
 * où il n'y a que le calendrier.
 */
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import {
  ajouterJours,
  calendrierDesExceptions,
  dec,
  estOuvre,
  formaterHeure,
  formaterDateCourte,
  formaterNombre,
} from "@planif/moteur";
import {
  chargerCalendrier,
  chargerCapacite,
  chargerGeneriques,
  chargerMateriels,
  chargerPlan,
} from "./api";
import type {
  BesoinDate,
  CapaciteJour,
  JourException,
  Materiel,
  PlanGeneral as Plan,
} from "./api";
import type { Generique } from "@planif/moteur";
import { SyntheseCapacite } from "./SyntheseCapacite";
import { PanneauPeriode } from "./Periode";
import type { Bornes } from "./Periode";

/** Les jours ouvrés de l'horizon, dans l'ordre. */
function joursOuvres(
  debut: string,
  fin: string,
  exceptions: readonly JourException[],
): readonly string[] {
  const calendrier = calendrierDesExceptions(exceptions);
  const jours: string[] = [];
  let courant = debut;
  // Borne de sécurité : un horizon mal formé ne doit pas faire tourner le navigateur
  // sans fin. Treize semaines est le maximum que le serveur accepte.
  for (let pas = 0; pas < 400 && courant <= fin; pas += 1) {
    if (estOuvre(courant, calendrier)) jours.push(courant);
    courant = ajouterJours(courant, 1);
  }
  return jours;
}

/** Les heures d'un ensemble de besoins — une livraison n'en porte aucune. */
function heuresDe(besoins: readonly BesoinDate[]): string {
  return besoins
    .reduce((somme, b) => (b.heures === null ? somme : somme.add(dec(b.heures))), dec(0))
    .toFixed();
}

export function PlanGeneral({
  onOuvrirChantier,
}: {
  onOuvrirChantier: (chantierId: string) => void;
}): JSX.Element {
  /**
   * La période demandée, ou `null` pour l'horizon par défaut du serveur.
   *
   * `null` n'est pas « aucune période » : c'est « celle que le serveur décide », soit
   * J-7 → J+80. L'écran ne calcule jamais ces dates lui-même — voir `Periode.tsx`.
   */
  const [periode, setPeriode] = useState<Bornes | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [exceptions, setExceptions] = useState<readonly JourException[]>([]);
  /**
   * De quoi confronter le besoin au disponible (métier, 16/08/2026).
   *
   * Trois lectures **séparées** du plan, et c'est délibéré : la capacité et le parc
   * viennent du référentiel maître, donc d'une autre application. Les charger avec le
   * plan ferait disparaître le planning général entier le jour où le référentiel ne
   * répond pas — alors que le besoin, lui, est calculé ici et reste juste.
   */
  const [capacite, setCapacite] = useState<readonly CapaciteJour[]>([]);
  const [materiels, setMateriels] = useState<readonly Materiel[]>([]);
  const [generiques, setGeneriques] = useState<readonly Generique[]>([]);
  const [erreurCapacite, setErreurCapacite] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  /** `null` = tous les codes. Sinon, on ne garde que celui-là dans le plan de charge. */
  const [codeSuivi, setCodeSuivi] = useState<string | null>(null);

  useEffect(() => {
    let abandonne = false;
    setChargement(true);
    Promise.all([chargerPlan(periode ?? undefined), chargerCalendrier()])
      .then(([reponse, calendrier]) => {
        if (abandonne) return;
        setPlan(reponse.plan);
        setExceptions(calendrier.exceptions);
        setErreur(null);
      })
      .catch((x: unknown) => {
        if (!abandonne) setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        if (!abandonne) setChargement(false);
      });
    return () => {
      abandonne = true;
    };
  }, [periode]);

  // Le parc et les codes génériques : ils ne bougent pas avec la période, donc une seule
  // lecture. Une panne n'est pas affichée — sans eux, la synthèse matériel est vide et le
  // reste de l'écran ne s'en aperçoit pas.
  useEffect(() => {
    chargerMateriels()
      .then((r) => {
        setMateriels(r.materiels);
      })
      .catch(() => undefined);
    chargerGeneriques()
      .then((r) => {
        setGeneriques(r.generiques);
      })
      .catch(() => undefined);
  }, []);

  /**
   * La capacité de la période affichée.
   *
   * Son erreur est **conservée et montrée dans la synthèse**, contrairement aux deux
   * référentiels ci-dessus : sans capacité, le tableau affiche un besoin sans rien en
   * face, et un taux d'utilisation absent se lirait comme « rien à signaler ». Il faut
   * dire pourquoi la colonne est vide.
   */
  useEffect(() => {
    if (plan === null) return;
    let abandonne = false;
    chargerCapacite(plan.debut, plan.fin)
      .then((r) => {
        if (abandonne) return;
        setCapacite(r.capacite);
        setErreurCapacite(null);
      })
      .catch((x: unknown) => {
        if (abandonne) return;
        setCapacite([]);
        setErreurCapacite(x instanceof Error ? x.message : String(x));
      });
    return () => {
      abandonne = true;
    };
  }, [plan]);

  const jours = useMemo(
    () => (plan === null ? [] : joursOuvres(plan.debut, plan.fin, exceptions)),
    [plan, exceptions],
  );

  /** Les besoins rangés par jour, puis par chantier — la maille de la frise. */
  const parJourEtChantier = useMemo(() => {
    const index = new Map<string, BesoinDate[]>();
    for (const besoin of plan?.besoins ?? []) {
      const cle = `${besoin.date}#${besoin.chantierId}`;
      const liste = index.get(cle) ?? [];
      liste.push(besoin);
      index.set(cle, liste);
    }
    return index;
  }, [plan]);

  /** Le plan de charge : par code générique, puis par jour. */
  const charge = useMemo(() => {
    const parCode = new Map<string, { libelle: string; estMateriel: boolean; jours: Map<string, BesoinDate[]> }>();
    for (const besoin of plan?.besoins ?? []) {
      // Une livraison ne consomme aucune capacité : 40 tonnes de grave n'occupent
      // personne. Les mêler aux heures ferait entrer des tonnes dans un total d'heures.
      if (besoin.heures === null) continue;
      const ligne = parCode.get(besoin.code) ?? {
        libelle: besoin.libelle,
        estMateriel: besoin.estMateriel,
        jours: new Map<string, BesoinDate[]>(),
      };
      const duJour = ligne.jours.get(besoin.date) ?? [];
      duJour.push(besoin);
      ligne.jours.set(besoin.date, duJour);
      parCode.set(besoin.code, ligne);
    }
    return [...parCode.entries()].sort((a, b) => a[0].localeCompare(b[0], "fr"));
  }, [plan]);

  return (
    <section className="plan">
      <p className="etat">
        Les contre-études validées, calées sur le calendrier réel. Les barres restent
        enregistrées en jours relatifs : décaler un chantier ne réécrit rien, cette vue se
        recalcule.
      </p>

      <PanneauPeriode
        affichee={plan === null ? null : { debut: plan.debut, fin: plan.fin }}
        chargement={chargement}
        onBorner={setPeriode}
        onDefaut={() => {
          setPeriode(null);
        }}
      />

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {chargement && <p className="etat">Chargement du planning général…</p>}

      {plan !== null && plan.sansDate.length > 0 && (
        <p className="alerte">
          {/*
            Nommés plutôt qu'ignorés : une charge qui disparaît en silence est une charge
            qu'on croit ne pas avoir, et le geste qui manque — saisir une date, fût-elle
            hypothèse — n'est évident que si l'écran le dit.
          */}
          {plan.sansDate.length === 1 ? "Un chantier validé n'a" : `${String(plan.sansDate.length)} chantiers validés n'ont`}{" "}
          aucune date de démarrage : {plan.sansDate.map((c) => `${c.apo} ${c.nom}`).join(", ")}. Ils
          ne pèsent nulle part tant qu'une date, même hypothèse, n'est pas saisie.
        </p>
      )}

      {plan !== null && !chargement && plan.chantiers.length === 0 && (
        <p className="etat">
          Aucune contre-étude validée sur cette période. Un chantier entre dans le planning
          général quand le responsable exploitation accepte sa contre-étude.
        </p>
      )}

      {plan !== null && plan.chantiers.length > 0 && (
        <>
          <h3>Les chantiers</h3>
          <div className="plan__defilement">
            <table className="plan__frise">
              <thead>
                <tr>
                  <th className="plan__entete-chantier">Chantier</th>
                  {jours.map((jour) => (
                    <th key={jour} className="plan__jour">
                      {formaterDateCourte(jour)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plan.chantiers.map((chantier) => (
                  <tr key={chantier.chantierId}>
                    <th className="plan__entete-chantier">
                      <button
                        type="button"
                        className="lien"
                        onClick={() => {
                          onOuvrirChantier(chantier.chantierId);
                        }}
                      >
                        {chantier.apo}
                      </button>{" "}
                      {chantier.nom}
                      {/* La distinction du §4.7 : une date hypothèse peut bouger, et c'est
                          précisément la variable d'ajustement du §4.8. */}
                      {chantier.dateDebutStatut === "hypothese" && (
                        <span className="plan__hypothese"> hypothèse</span>
                      )}
                    </th>
                    {jours.map((jour) => {
                      const duJour = parJourEtChantier.get(`${jour}#${chantier.chantierId}`) ?? [];
                      if (duJour.length === 0) return <td key={jour} className="plan__vide" />;
                      const heures = heuresDe(duJour);
                      return (
                        <td
                          key={jour}
                          className={
                            chantier.dateDebutStatut === "hypothese"
                              ? "plan__occupe plan__occupe--hypothese"
                              : "plan__occupe"
                          }
                          title={`${chantier.apo} — ${jour} : ${formaterHeure(heures)} h, ${String(duJour.length)} ligne(s)`}
                        >
                          {formaterNombre(heures, 0)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            **La synthèse s'intercale entre les chantiers et le plan de charge** (métier,
            16/08/2026), et cet ordre est la lecture même : *où sont les chantiers*, puis
            *est-ce que ça tient*, puis *le détail heure par heure*. La mettre après le
            plan de charge obligerait à traverser cent lignes pour savoir si l'on est en
            dépassement.
          */}
          <SyntheseCapacite
            besoins={plan.besoins}
            capacite={capacite}
            materiels={materiels}
            generiques={generiques}
            jours={jours}
            erreurCapacite={erreurCapacite}
          />

          <h3>Plan de charge — heures par ressource générique</h3>
          <p className="etat">
            Une demi-journée posée vaut 4 h par exemplaire engagé : c'est la grandeur que le
            référentiel appelle capacité (effectif × 8 h). Les livraisons n'y figurent pas —
            elles n'occupent personne.
            {codeSuivi !== null && (
              <>
                {" "}
                <button
                  type="button"
                  className="lien"
                  onClick={() => {
                    setCodeSuivi(null);
                  }}
                >
                  Tout afficher
                </button>
              </>
            )}
          </p>
          <div className="plan__defilement">
            <table className="plan__charge">
              <thead>
                <tr>
                  <th className="plan__entete-chantier">Code</th>
                  {jours.map((jour) => (
                    <th key={jour} className="plan__jour">
                      {formaterDateCourte(jour)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {charge
                  .filter(([code]) => codeSuivi === null || code === codeSuivi)
                  .map(([code, ligne]) => (
                    <tr key={code}>
                      <th className="plan__entete-chantier">
                        <button
                          type="button"
                          className="lien"
                          onClick={() => {
                            setCodeSuivi(code);
                          }}
                        >
                          {code}
                        </button>
                        <span className="note"> {ligne.libelle}</span>
                      </th>
                      {jours.map((jour) => {
                        const duJour = ligne.jours.get(jour) ?? [];
                        if (duJour.length === 0) return <td key={jour} className="plan__vide" />;
                        const heures = heuresDe(duJour);
                        return (
                          <td
                            key={jour}
                            className={ligne.estMateriel ? "plan__charge-mat" : "plan__charge-mo"}
                            title={duJour
                              .map((b) => `${b.poste} · ${formaterHeure(b.heures ?? "0")} h`)
                              .join("\n")}
                          >
                            {formaterNombre(heures, 0)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                <tr className="plan__total">
                  <th className="plan__entete-chantier">Total</th>
                  {jours.map((jour) => {
                    const duJour = (plan.besoins as readonly BesoinDate[]).filter(
                      (b) => b.date === jour && b.heures !== null,
                    );
                    return (
                      <td key={jour}>
                        {duJour.length === 0 ? "" : formaterNombre(heuresDe(duJour), 0)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
